import { KeyValue } from '@angular/common';
import {
	Component,
	DoCheck,
	ElementRef,
	EventEmitter,
	Input,
	IterableDiffer,
	IterableDiffers,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import Fuse from 'fuse.js';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

import { createSearchSelectData, ISearchSelectData, SearchSelectDataUnion } from './search-select.models';

@Component({
	selector: 'app-search-select',
	templateUrl: './search-select.component.html',
	styleUrls: ['./search-select.component.scss']
})
export class SearchSelectComponent implements OnInit, OnChanges, DoCheck {
	@ViewChild('searchInput') searchInput: ElementRef;
	@ViewChild('selectButton') selectButton: MatButton;

	@Output() selectedChange = new EventEmitter<string | number>();

	@Input() data: SearchSelectDataUnion[] = [];
	@Input() selected: string | number;

	// Configuration
	@Input() disabled = false;
	@Input() title: string; // Button title
	@Input() placeholder: string; // Input placeholder
	@Input() tooltip: string; // Button tooltip text
	@Input() metaSort: string[] = []; // Meta attributes to sort on, empty use default order
	@Input() metaSearch: string[] = []; // Meta attributes to search on, empty search on item name
	@Input() btnColor: 'primary' | 'accent' = 'primary'; // Color scheme for button
	@Input() disableSelect = false;

	private sortedData: ISearchSelectData[] = [];
	private iterableDiffer: IterableDiffer<any>;
	public filteredData: Observable<ISearchSelectData[]>;
	public searchControl = new FormControl('');
	public open = false;
	public selectedOption: ISearchSelectData;

	constructor(private iterableDiffers: IterableDiffers) {}

	public ngOnInit(): void {
		this.iterableDiffer = this.iterableDiffers.find([]).create(null);

		this.filteredData = this.searchControl.valueChanges.pipe(
			debounceTime(500),
			startWith(''),
			map((value) => this.filterData(value))
		);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes?.data?.currentValue) {
			this.transformData(changes.data.currentValue);
		}

		if (changes?.selected) {
			const currentValue = this.sortedData.find((item) => item.id === changes.selected.currentValue);

			if (currentValue) {
				this.selectedOption = !this.disableSelect ? currentValue : null;
				this.selected = changes.selected.currentValue;
			} else {
				this.selectedOption = null;
				this.selected = null;
			}
		}
	}

	public ngDoCheck(): void {
		if (this.iterableDiffer.diff(this.data)) {
			this.transformData(this.data);
		}
	}

	// Used to maintain keyvalue pipe natural order rather than the default key order
	public comparator(a: KeyValue<number, string>, b: KeyValue<number, string>): number {
		return 0;
	}

	public inputBlur(): void {
		this.searchInput?.nativeElement?.blur();
		this.selectButton._elementRef.nativeElement.focus();
	}

	public closeSearch(): void {
		if (!this.data || this.data.length === 0) {
			this.searchClosed();
		}
	}

	public triggerSearch(): void {
		this.open = true;

		// Focus input element, leave in timeout to ensure UI has time to render input field before settings focus
		setTimeout(() => {
			this.searchInput?.nativeElement?.focus();
		});
	}

	public handleSpaceEvent(event: KeyboardEvent) {
		this.searchControl.setValue(this.searchControl.value + ' ');
		event.preventDefault();
	}

	public searchClosed(): void {
		this.open = false;
		this.searchControl.setValue('');
	}

	public optionSelected(event: MatAutocompleteSelectedEvent): void {
		this.selectedOption = !this.disableSelect ? this.sortedData.find((item) => item.id === event.option.value) : null;
		this.selected = event.option.value;
		this.selectedChange.emit(this.selected);
		this.searchInput.nativeElement.blur();
		this.searchControl.setValue('');
		this.open = false;
		this.selectButton._elementRef.nativeElement.focus();
	}

	private transformData(data: SearchSelectDataUnion[]): void {
		const sortedData: ISearchSelectData[] = [];

		data.forEach((item) => sortedData.push(createSearchSelectData(item)));

		sortedData.sort((a: ISearchSelectData, b: ISearchSelectData) => {
			return a.name?.localeCompare(b.name);
		});

		this.metaSort.forEach((attr) => {
			sortedData.sort((a: ISearchSelectData, b: ISearchSelectData) => {
				return a.meta[attr].name?.localeCompare(b.meta[attr].name);
			});
		});

		this.sortedData = sortedData;
		this.searchControl.setValue(this.searchControl.value);
	}

	private filterData(value: string): ISearchSelectData[] {
		const query = value?.trim();

		if (query) {
			const fuseOptions = {
				ignoreLocation: true,
				keys: ['name']
			};

			if (this.metaSearch.length > 0) {
				fuseOptions.keys = fuseOptions.keys.concat(this.metaSearch.map((key) => `meta.${key}.name`));
			}

			const fuse = new Fuse(this.sortedData, fuseOptions);

			return fuse.search(query).map((res) => {
				return { ...res.item };
			});
		}

		return this.sortedData;
	}
}
